import React, { useState, useEffect } from "react";
import { withStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Typography from "@material-ui/core/Typography";
import {
  TextField,
  Button,
  Switch,
  Grid,
} from "@material-ui/core";
import AutoSelect from "../InputComponents/AutoSelect";
import AddIcon from "@mui/icons-material/Add";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import styles from "./components.module.scss";
import DeleteIcon from "../../Assets/DeleteIcon-red.svg";
import { InsertDriveFile } from "@mui/icons-material";
import * as XLSX from "xlsx";

// Reusable FormField Component
const FormField = ({ label, children, className = "" }) => (
  <div className={`${styles.formField} ${className}`}>
    {label && <label className={styles.formLabel}>{label}</label>}
    {children}
  </div>
);

// Reusable DropdownOptionRow Component
const DropdownOptionRow = ({
  option,
  index,
  onChange,
  onDelete,
  canDelete,
}) => (
  <div className={styles.dropdownOptionRow}>
    <div className={styles.optionLabel}>{String.fromCharCode(65 + index)})</div>
    <StyledTextField
      variant="outlined"
      placeholder={`Option ${String.fromCharCode(65 + index)}`}
      value={option.value}
      onChange={(e) => onChange(option.id, e.target.value)}
      className={styles.optionInput}
      inputProps={{ maxLength: 50 }}
    />
    {canDelete && (
      <IconButton
        size="small"
        onClick={() => onDelete(option.id)}
        className={styles.deleteOptionButton}
      >
        <img src={DeleteIcon} alt="Delete" style={{ width: 20, height: 20 }} />
      </IconButton>
    )}
  </div>
);

// Attribute Type Options Configuration
const ATTRIBUTE_TYPE_OPTIONS = [
  { value: "dropdown", name: "Dropdown" },
  { value: "free_text", name: "Free Text" },
  { value: "single_image", name: "Single File" },
  { value: "multiple_image", name: "Multiple File" },
  { value: "options_list", name: "Option List" },
];

// Reusable TextField Props
const commonTextFieldProps = {
  variant: "outlined",
};

// Maximum number of dropdown options allowed
const MAX_DROPDOWN_OPTIONS = 6;

const dialogTitleStyles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(3.25),
    paddingBottom: theme.spacing(3),
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: "18px",
    color: "#2E2E2E",
  },
});

const DialogTitle = withStyles(dialogTitleStyles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6" className={styles.modalTitle}>
        {children}
      </Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(3.25),
    paddingTop: theme.spacing(0),
    borderBottom: "unset",
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(3.25),
    justifyContent: "space-between",
  },
}))(MuiDialogActions);

const StyledSwitch = withStyles((theme) => ({
  switchBase: {
    color: "#ffffff",
    "&$checked": {
      color: "#1c4980",
    },
    "&$checked + $track": {
      backgroundColor: "#1c4980",
    },
  },
  checked: {},
  track: {
    backgroundColor: "#9F9F9F",
  },
}))(Switch);

const StyledTextField = withStyles((theme) => ({
  root: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "6px",
      "& fieldset": {
        borderColor: "#E4E4E4",
        borderWidth: "0.8px",
      },
      "&:hover fieldset": {
        borderColor: "#E4E4E4",
        borderWidth: "0.8px",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#1c4980",
        borderWidth: "1px",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "14px 16px",
      fontSize: "14px",
      "&::placeholder": {
        color: "#757575",
        opacity: 1,
      },
    },
  },
}))(TextField);

const AddAttributeModal = ({
  open,
  onClose,
  onSave,
  attributeType = "checkin",
  editingAttribute = null,
}) => {
  const [attributeName, setAttributeName] = useState("");
  const [attributeTypeValue, setAttributeTypeValue] = useState({ value: "", name: "" });
  const [dropdownOptions, setDropdownOptions] = useState([
    { id: 1, value: "" },
    { id: 2, value: "" },
  ]);
  const [isRequired, setIsRequired] = useState(true);
  const [optionListFile, setOptionListFile] = useState(null);

  useEffect(() => {
    if (editingAttribute) {
      setAttributeName(editingAttribute.attributeName || "");
      setAttributeTypeValue(
        ATTRIBUTE_TYPE_OPTIONS.find((o) => o.value === editingAttribute.attributeType) ||
        ATTRIBUTE_TYPE_OPTIONS[0]
      );
      setIsRequired(editingAttribute.isRequired ? true : false);
      if (
        editingAttribute.dropdownOptions &&
        editingAttribute.dropdownOptions.length > 0
      ) {
        setDropdownOptions(
          editingAttribute.dropdownOptions.map((opt, idx) => ({
            id: idx + 1,
            value: opt,
          }))
        );
      }
      if (editingAttribute.attributeType === "options_list") {
        const ws = XLSX.utils.aoa_to_sheet([["Options"], ...editingAttribute.dropdownOptions?.map(opt => [opt])]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
        const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const file = new File([wbout], `Options.xlsx`, {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        setOptionListFile(file);
      }
    } else {
      resetForm();
    }
  }, [editingAttribute, open]);

  const resetForm = () => {
    setAttributeName("");
    setAttributeTypeValue({ value: "", name: "" });
    setDropdownOptions([
      { id: 1, value: "" },
      { id: 2, value: "" },
    ]);
    setIsRequired(true);
    setOptionListFile(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleAddOption = () => {
    if (dropdownOptions.length >= MAX_DROPDOWN_OPTIONS) {
      return;
    }
    const newId =
      dropdownOptions.length > 0
        ? Math.max(...dropdownOptions.map((opt) => opt.id)) + 1
        : 1;
    setDropdownOptions([...dropdownOptions, { id: newId, value: "" }]);
  };

  const handleDeleteOption = (id) => {
    if (dropdownOptions.length > 1) {
      setDropdownOptions(dropdownOptions.filter((opt) => opt.id !== id));
    }
  };

  const handleOptionChange = (id, value) => {
    setDropdownOptions(
      dropdownOptions.map((opt) => (opt.id === id ? { ...opt, value } : opt))
    );
  };

  const handleSave = () => {
    const attributeData = {
      attributeName: attributeName.trim(),
      attributeType: attributeTypeValue?.value,
      isRequired: isRequired ? 1 : 0,
    };

    if (attributeTypeValue?.value === "dropdown") {
      const filteredOptions = dropdownOptions
        .map((opt) => opt.value.trim())
        .filter((v) => v);

      if (filteredOptions.length === 0) {
        return;
      }

      attributeData.dropdownOptions = filteredOptions;
    }

    if (!attributeData.attributeName || !attributeData.attributeType) {
      return;
    }

    onSave(attributeData, attributeType, optionListFile);
    handleClose();
  };

  const isSaveDisabled =
    !attributeName.trim() ||
    (attributeTypeValue?.value === "dropdown" &&
      dropdownOptions.filter((opt) => opt.value.trim()).length === 0) || (attributeTypeValue?.value === "options_list" && !optionListFile);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      classes={{
        paper: styles.modalPaper,
      }}
    >
      <DialogTitle onClose={handleClose}>Add New Attributes</DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {/* Attribute Name Field */}
          <Grid item xs={12}>
            <FormField label="Attribute Name">
              <StyledTextField
                {...commonTextFieldProps}
                fullWidth
                placeholder="Add Attribute.."
                value={attributeName}
                onChange={(e) => setAttributeName(e.target.value)}
              />
            </FormField>
          </Grid>

          {/* Attribute Type Field */}
          <Grid item xs={12}>
            <FormField label="Attribute Type">
              <AutoSelect
                list={ATTRIBUTE_TYPE_OPTIONS}
                data={{ attributeType: attributeTypeValue }}
                setData={(updated) => setAttributeTypeValue(updated.attributeType)}
                id="attributeType"
                multipleFlag={false}
                placeHolder="Attribute Type"
              />
            </FormField>
          </Grid>

          {/* Option List Upload Field */}
          {attributeTypeValue?.value === "options_list" && (
            <Grid item xs={12}>
              <FormField>
                <div className={styles.uploadRow}>
                  <Button
                    variant="outlined"
                    className={styles.uploadExcelButton}
                    startIcon={<InsertDriveFile style={{ color: "#1c4980" }} />}
                    component="label"
                  >
                    Upload Excel
                    <input
                      type="file"
                      hidden
                      accept=".xls,.xlsx"
                      onChange={(e) => setOptionListFile(e.target.files[0] || null)}
                    />
                  </Button>
                  {optionListFile && (
                    <div className={styles.fileChip}>
                      <a
                        href={URL.createObjectURL(optionListFile)}
                        download={optionListFile.name}
                        className={styles.fileChipName}
                      >
                        {optionListFile.name.replace(/\.[^.]+$/, "")}
                      </a>
                      <IconButton
                        size="small"
                        className={styles.fileChipClose}
                        onClick={() => setOptionListFile(null)}
                      >
                        <CloseRoundedIcon style={{ fontSize: 16 }} />
                      </IconButton>
                    </div>
                  )}
                </div>
                <span className={styles.uploadHint}>Only .xls and .xlsx are allowed</span>
              </FormField>
            </Grid>
          )}

          {/* Dropdown Options Field */}
          {attributeTypeValue?.value === "dropdown" && (
            <Grid item xs={12}>
              <FormField>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <label className={styles.formLabel}>Dropdown Option</label>
                  {dropdownOptions.length < MAX_DROPDOWN_OPTIONS && (
                    <Button
                      startIcon={<AddIcon style={{ color: "#2E2E2E" }} />}
                      onClick={handleAddOption}
                      className={styles.addOptionButton}
                    >
                      Add Option
                    </Button>
                  )}
                </div>
                <div className={styles.dropdownOptionsContainer}>
                  {dropdownOptions.map((option, index) => (
                    <DropdownOptionRow
                      key={option.id}
                      option={option}
                      index={index}
                      onChange={handleOptionChange}
                      onDelete={handleDeleteOption}
                      canDelete={dropdownOptions.length > 2}
                    />
                  ))}
                </div>
              </FormField>
            </Grid>
          )}

          {/* Requirement Field */}
          <Grid item xs={12}>
            <FormField>
              <div className={styles.requirementSection}>
                <div className={styles.requirementLabelContainer}>
                  <label className={styles.formLabel}>Requirement</label>
                  <span className={styles.requirementDescription}>
                    This field is optional
                  </span>
                </div>
                <StyledSwitch
                  checked={!isRequired}
                  onChange={(e) => setIsRequired(!e.target.checked)}
                />
              </div>
            </FormField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={resetForm}
          className={styles.clearButton}
          variant="outlined"
        >
          Clear
        </Button>
        <Button
          onClick={handleSave}
          className={styles.saveButton}
          variant="contained"
          disabled={isSaveDisabled}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddAttributeModal;
